import { useMemo, useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useAuth, useRole, useStaffRole, useStaffPermissions } from '@structural-climbing/auth'
import {
  APP_VERSIONS,
  Avatar,
  Button,
  Chip,
  Divider,
  Select,
  SelectItem,
  UpdateBanner,
  usePwaInstall,
} from '@structural-climbing/ui'
import { GymContextProvider, useGymContext } from '../contexts/GymContext'
import { tierIncludesCompetitions } from '../lib/tiers'
import { GymStandingBanner } from '../components/GymStandingBanner'
import { APP_PRIMARY, GymThemeContext } from './useGymTheme'
import { GymGradeConfigContext } from './useGymGradeConfig'
import { GymDisciplinesContext } from './useGymDisciplines'
import { useRealtimePendingRequests } from '../features/requests/useRealtimePendingRequests'
import { useStaffGyms } from './useStaffGyms'
import { ALL_NAV_ITEMS, NAV_GROUPS, type NavItem } from './navItems'
import { NavIcon } from './NavIcon'
import { GymLogo } from './GymLogo'
import { useNavFavourites } from './useNavFavourites'
import { TopBarSlotContext } from './topBarSlot'
import { useIsMobile } from '../lib/useIsMobile'
import { QuickScanProvider, ScanFab } from '../features/checkIn/QuickScan'

function GymSidebar() {
  const { logout, user } = useAuth()
  const role = useRole()
  const staffRole = useStaffRole()
  const staffPermissions = useStaffPermissions()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [editingFavourites, setEditingFavourites] = useState(false)
  const [topBarSlot, setTopBarSlot] = useState<HTMLDivElement | null>(null)
  const { canInstall, install } = usePwaInstall()
  const gym = useGymContext()
  const { t } = useTranslation()
  const location = useLocation()
  const isMobile = useIsMobile()
  const hasPendingRequests = useRealtimePendingRequests(gym.gymId)
  const { gyms: staffGyms, switching, switchGym } = useStaffGyms()
  const { favourites: favouritePaths, toggle, moveUp } = useNavFavourites(user?.sub, gym.gymId)

  // An owner is only ever the owner. Staff show the job they were invited as,
  // plus Trainer once they hold My Members — the same rule as the Team page.
  const roleLabels: string[] = []
  if (role === 'gym_owner') {
    roleLabels.push(t('profile.roleOwner'))
  } else {
    if (staffRole === 'front_desk') roleLabels.push(t('team.roleFrontDesk'))
    if (staffRole === 'route_setter') roleLabels.push(t('team.roleRouteSetter'))
    if (staffRole === 'trainer' || staffPermissions?.my_members === true) {
      roleLabels.push(t('team.roleTrainer'))
    }
  }

  const visibleItems = useMemo(() => {
    // A plan-gated item is hidden outright rather than shown and refused: an
    // owner who cannot use it does not need it taking up the sidebar.
    const onPlan = (item: NavItem) =>
      (!item.needsCompetitions || tierIncludesCompetitions(gym.planTier)) &&
      (!item.needsProducts || gym.productsEnabled)

    if (role === 'gym_owner') return ALL_NAV_ITEMS.filter(onPlan)
    if (role === 'gym_staff' && staffPermissions !== null) {
      return ALL_NAV_ITEMS.filter(
        (item) =>
          !item.ownerOnly &&
          item.staffKey &&
          (staffPermissions[item.staffKey] === true ||
            (item.orStaffKey !== undefined && staffPermissions[item.orStaffKey] === true)) &&
          onPlan(item),
      )
    }
    return []
  }, [role, staffPermissions, gym.planTier, gym.productsEnabled])

  // Favourites keep the order the person chose; everything else sits in its
  // job section, in the fixed order of ALL_NAV_ITEMS.
  const favourites = favouritePaths
    .map((path) => visibleItems.find((item) => item.path === path))
    .filter((item): item is NavItem => item !== undefined)
  const sections = NAV_GROUPS.map((group) => ({
    ...group,
    items: visibleItems.filter(
      (item) => item.group === group.key && !favouritePaths.includes(item.path),
    ),
  })).filter((section) => section.items.length > 0)

  const currentPath = location.pathname.split('/')[2] ?? ''
  // Anyone who may open Check-in may scan a member from any page on a phone.
  const canScan = visibleItems.some((item) => item.path === 'check-in')
  // On every page, Dashboard and Check-in included (they also have a big scan card).
  const showScanFab = isMobile && canScan
  const currentItem = ALL_NAV_ITEMS.find((item) => item.path === currentPath)
  const isActive = (item: NavItem) => item.path === currentPath

  const requestsDot = (item: NavItem) =>
    item.path === 'requests' && hasPendingRequests ? (
      <span className="ml-auto h-2 w-2 flex-shrink-0 rounded-full bg-danger" />
    ) : null

  const renderNavLink = (item: NavItem) => {
    const label = t(item.labelKey)
    const favouriteIndex = favouritePaths.indexOf(item.path)
    const isFavourite = favouriteIndex >= 0
    return (
      <div key={item.path} className="flex items-center gap-0.5">
        <NavLink
          to={item.path}
          onClick={() => setSidebarOpen(false)}
          className={({ isActive: active }) =>
            `flex min-w-0 flex-1 items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-colors ${
              active
                ? 'bg-primary/10 font-semibold text-primary'
                : 'text-foreground/70 hover:bg-content2 hover:text-foreground'
            }`
          }
        >
          <NavIcon name={item.icon} />
          <span className="truncate">{label}</span>
          {requestsDot(item)}
        </NavLink>
        {editingFavourites && isFavourite && favouriteIndex > 0 && (
          <Button
            isIconOnly
            size="sm"
            variant="light"
            className="h-7 w-7 min-w-7 text-foreground/50"
            aria-label={t('nav.moveFavouriteUp', { page: label })}
            onPress={() => moveUp(item.path)}
          >
            <NavIcon name="up" size={15} />
          </Button>
        )}
        {editingFavourites && (
          <Button
            isIconOnly
            size="sm"
            variant="light"
            className={`h-7 w-7 min-w-7 ${isFavourite ? 'text-warning' : 'text-foreground/40'}`}
            aria-label={
              isFavourite
                ? t('nav.removeFavourite', { page: label })
                : t('nav.addFavourite', { page: label })
            }
            aria-pressed={isFavourite}
            onPress={() => toggle(item.path)}
          >
            <NavIcon name="star" size={16} filled={isFavourite} />
          </Button>
        )}
      </div>
    )
  }

  const groupLabelClass =
    'px-2.5 pb-1 pt-4 text-tiny font-semibold uppercase tracking-wider text-foreground/50'

  return (
    <GymThemeContext.Provider value={{ primaryColor: APP_PRIMARY, secondaryColor: null }}>
      <GymGradeConfigContext.Provider value={gym.gradeConfig}>
        <GymDisciplinesContext.Provider value={gym.disciplines}>
          <div className="flex h-screen bg-[#F5F3F0] text-foreground">
            {sidebarOpen && (
              <div
                className="fixed inset-0 z-40 bg-black/50 md:hidden"
                onClick={() => setSidebarOpen(false)}
              />
            )}
            <aside
              className={`fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-divider bg-white px-3 py-4 md:bg-white/80 md:backdrop-blur-sm transition-transform duration-200 md:relative md:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
            >
              <div className="mb-2 flex items-center gap-3 px-1">
                <GymLogo name={gym.name} logoUrl={gym.logoUrl} />
                <div className="min-w-0 flex-1">
                  {staffGyms.length > 1 ? (
                    <Select
                      size="sm"
                      aria-label="Switch gym"
                      selectedKeys={[gym.gymId]}
                      isDisabled={switching}
                      onSelectionChange={(keys) => {
                        const targetId = [...(keys as Iterable<React.Key>)][0] as string | undefined
                        if (!targetId || targetId === gym.gymId) return
                        const target = staffGyms.find((g) => g.gym_id === targetId)
                        if (target) void switchGym(target.gym_id, target.slug)
                      }}
                    >
                      {staffGyms.map((g) => (
                        <SelectItem key={g.gym_id}>{g.name}</SelectItem>
                      ))}
                    </Select>
                  ) : (
                    <p className="truncate font-semibold">{gym.name}</p>
                  )}
                  <p className="text-tiny text-foreground/50">{t('brand.gymPortal')}</p>
                </div>
              </div>
              <nav className="-mx-1 flex min-h-0 flex-1 flex-col overflow-y-auto px-1">
                {favourites.length > 0 || editingFavourites ? (
                  <>
                    <div className="flex items-center justify-between pl-2.5 pt-2">
                      <span className="flex items-center gap-1.5 text-tiny font-semibold uppercase tracking-wider text-foreground/50">
                        <NavIcon name="star" size={11} />
                        {t('nav.favourites')}
                      </span>
                      <Button
                        size="sm"
                        variant="light"
                        color="primary"
                        className="h-6 min-w-0 px-2 text-tiny font-semibold"
                        onPress={() => setEditingFavourites((v) => !v)}
                      >
                        {editingFavourites ? t('common.done') : t('nav.editFavourites')}
                      </Button>
                    </div>
                    {favourites.map(renderNavLink)}
                    {favourites.length === 0 && (
                      <p className="px-2.5 py-1 text-tiny text-foreground/50">
                        {t('nav.favouritesEmpty')}
                      </p>
                    )}
                    {editingFavourites && (
                      <p className="px-2.5 pt-1 text-tiny text-foreground/50">
                        {t('nav.favouritesHint')}
                      </p>
                    )}
                  </>
                ) : null}
                {favourites.length > 0 && sections.length > 0 && (
                  <Divider className="mx-1.5 mt-3 w-auto" />
                )}
                {sections.map((section) => (
                  <div key={section.key} className="flex flex-col">
                    <p className={groupLabelClass}>{t(section.labelKey)}</p>
                    {section.items.map(renderNavLink)}
                  </div>
                ))}
              </nav>
              <div className="mt-3 border-t border-divider pt-3">
                <div className="flex items-center gap-2.5 px-1">
                  <Avatar
                    size="sm"
                    color="primary"
                    name={(user?.name ?? user?.email ?? '?').charAt(0).toUpperCase()}
                    className="flex-shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{user?.name}</p>
                    <p className="truncate text-xs text-foreground/50">{user?.email}</p>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5 px-1">
                  {roleLabels.map((label) => (
                    <Chip key={label} size="sm" variant="flat" radius="sm">
                      {label}
                    </Chip>
                  ))}
                </div>
              </div>
              <div className="mt-3 flex flex-col gap-2">
                {canInstall && (
                  <Button size="sm" variant="flat" color="primary" onPress={install}>
                    {t('common.installApp')}
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="flat"
                  color="danger"
                  onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                >
                  {t('common.signOut')}
                </Button>
              </div>
              <div className="mt-2 flex items-center justify-center gap-1.5 text-tiny text-default-400">
                <img src="/icons/symbol-structural.png" alt="Structural" className="h-3.5 w-auto" />
                <span>v{APP_VERSIONS.gym}</span>
              </div>
            </aside>
            <main className="flex min-w-0 flex-1 flex-col overflow-auto">
              <UpdateBanner message={t('appUpdate.message')} actionLabel={t('appUpdate.action')} />
              {isMobile && (
                <div className="sticky top-0 z-30 border-b border-divider bg-white/85 backdrop-blur-md">
                  <div className="flex items-center gap-2 px-3 pb-1.5 pt-2">
                    <Button
                      isIconOnly
                      variant="light"
                      size="sm"
                      aria-label={t('nav.openMenu')}
                      onPress={() => setSidebarOpen(true)}
                    >
                      <NavIcon name="menu" size={20} />
                    </Button>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-base font-semibold leading-tight">
                        {currentItem ? t(currentItem.labelKey) : gym.name}
                      </p>
                      <p className="truncate text-tiny text-foreground/50">{gym.name}</p>
                    </div>
                    <div ref={setTopBarSlot} className="flex items-center gap-1" />
                    <GymLogo name={gym.name} logoUrl={gym.logoUrl} className="h-8 w-8 text-tiny" />
                  </div>
                  <div className="flex gap-1.5 overflow-x-auto px-3 pb-2 [scrollbar-width:none]">
                    {favourites.map((item) => (
                      <Button
                        key={item.path}
                        as={NavLink}
                        to={item.path}
                        size="sm"
                        radius="full"
                        variant={isActive(item) ? 'solid' : 'flat'}
                        color={isActive(item) ? 'primary' : 'default'}
                        className="flex-none"
                        startContent={<NavIcon name={item.icon} size={14} />}
                        endContent={requestsDot(item)}
                      >
                        {t(item.labelKey)}
                      </Button>
                    ))}
                    <Button
                      size="sm"
                      radius="full"
                      variant="flat"
                      className="flex-none"
                      startContent={<NavIcon name="grid" size={14} />}
                      onPress={() => setSidebarOpen(true)}
                    >
                      {t('nav.allPages')}
                    </Button>
                  </div>
                </div>
              )}
              <GymStandingBanner />
              <TopBarSlotContext.Provider value={isMobile ? topBarSlot : null}>
                <QuickScanProvider canScan={canScan}>
                  <Outlet />
                  {showScanFab && <ScanFab />}
                </QuickScanProvider>
              </TopBarSlotContext.Provider>
            </main>
          </div>
        </GymDisciplinesContext.Provider>
      </GymGradeConfigContext.Provider>
    </GymThemeContext.Provider>
  )
}

export function GymLayout() {
  return (
    <GymContextProvider>
      <GymSidebar />
    </GymContextProvider>
  )
}
